import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { FetchFn, fetchWithAuth } from '../fetch';
import { MedisanteDevice, MedisanteMetric } from '../pages/Device/types';
import { Filter } from '../pages/Devices/types';
import { measurementTypeSpecialQuery } from '../pages/Devices/components/Filters/queryString';
import { validateFilters } from '../pages/Devices/config';
import { MedisanteTargetSystem } from '../pages/TargetSystems/types';
import { categories } from '../pages/Alerts/constants';
import { MedisanteOrgReport } from '../pages/Dashboard/types';
import { isTotalMeasurementCount, onlyUnique } from '../utils/helpers';
import {
  AuthUser,
  hasOneOfGlobalRoles,
  hasOneOfOrgRoles,
  Org,
  Role,
} from '../contexts/AuthProvider/types';
import { TagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Config } from '../eliot-components/src/ConfigProvider/types';

interface BaseApiArg {
  apiBaseURL: string;
}

export interface TargetSystemsArg extends BaseApiArg {
  orgs: string[];
  filters?: Filter[];
}

export interface SendTestMeasurementArg extends BaseApiArg {
  targetSystemId: string;
  deviceModel: string;
  measurementType: string;
}

export interface GetReportsArg extends BaseApiArg {
  orgs: string[];
  filters?: Filter[];
}

export interface GetMetricsArg extends BaseApiArg {
  deviceID: string;
}

export interface GetDevicesArg extends BaseApiArg {
  orgs: string[];
  filters: Filter[];
}

export interface GetDeviceArg extends BaseApiArg {
  id: string | null;
}

export interface GetUserArg extends BaseApiArg {
  id: string;
  token: { [id: string]: any } | null;
}

export interface UpdateUserArg extends BaseApiArg {
  id: string;
  data: {
    globalRolesModeEnabled?: boolean;
  };
}

export interface GetDevicesByIdArg extends BaseApiArg {
  ids: string[];
}

export interface PinDeviceArg extends BaseApiArg {
  orgId: string;
  isPinned: boolean;
  deviceIDs: string[];
}

export interface CreateOrgArg extends BaseApiArg {
  name: string;
}

export interface DeleteOrgArg extends BaseApiArg {
  id: string;
}

export interface UpdateOrgArg extends BaseApiArg {
  org: Org;
}

export interface ArchiveDeviceArg extends BaseApiArg {
  orgId: string;
  isArchived: boolean;
  deviceIDs: string[];
}

export interface RemoveTsDevicesArg extends BaseApiArg {
  id: string;
}

export interface TransferOwner extends BaseApiArg {
  deviceIDs: string[];
  targetSystemIDs: string[];
  owner: string;
  orgID: string;
}

export interface DeleteDeviceArg extends BaseApiArg {
  id: string;
}

interface CreateTsArg extends BaseApiArg {
  orgs: string[];
  targetSystem: any;
}

interface CreateCareTsArg extends BaseApiArg {
  org: string;
}

interface EditTsArg extends BaseApiArg {
  targetSystem: any;
}

interface MessageResponse {
  message: string;
}

export interface TSCreateWarnings {
  warnings?: string[];
  warnMessage?: string;
  message: string;
}

interface ChangeWeightUnitArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  weightUnit: string;
}

interface ChangeMinWeightArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  minWeight: number;
}

interface ChangeTimezoneArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  timeZone: string;
}

interface ChangeTargetSystemArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  targetSystemIDs: string[];
}

interface ChangeDeviceFlagArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  flags: string[];
}

interface ResetDeviceTokenArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
}

interface LinkDeviceArg extends BaseApiArg {
  deviceIds: MedisanteDevice[];
  gatewayId: string;
}

interface ChangeDeviceTestArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  isTest: boolean;
}

export interface StockDeviceArg extends BaseApiArg {
  devices: MedisanteDevice[];
  stock: boolean;
}

interface ConfigureHeartbeatSyncArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  timerDuration: number;
  retryCount: number;
  retryCountTime: number;
}

export interface BlockUnblockArg extends BaseApiArg {
  devices: MedisanteDevice[];
  block: boolean;
}

interface MuteDeviceArg extends BaseApiArg {
  devices: MedisanteDevice[];
  orgIDs: string[];
  mute: boolean;
}

export interface UnlinkedMode extends BaseApiArg {
  devices: MedisanteDevice[];
  allow: boolean;
}

export interface ResetMeasurementCountArg extends BaseApiArg {
  devices: MedisanteDevice[];
}

const getRequestQuery = (orgs: string[], filters: Filter[]) => {
  const isTotalCount = isTotalMeasurementCount();
  const orgsSearchQuery = orgs.map((o) => `orgs=${o}`).sort();
  const filtersSearchQuery = filters.map((f) => {
    let category = f.category;
    let value =
      f.category === 'propertyStatus' ? f.value.toLowerCase() : f.value;
    if (category === 'measurementType') {
      value = measurementTypeSpecialQuery(value);
    }
    switch (category) {
      case 'measurementEquals':
        category = isTotalCount ? category : 'measurementTripEquals';
        break;
      case 'measurementGreaterThan':
        category = isTotalCount ? category : 'measurementTripGreaterThan';
        break;
      case 'measurementLessThan':
        category = isTotalCount ? category : 'measurementTripLessThan';
        break;
    }

    return `${encodeURIComponent(category)}=${encodeURIComponent(value)}`;
  });

  return [...orgsSearchQuery, ...filtersSearchQuery].join('&');
};

async function requestWrapper<T>(
  url: string,
  reqOptions: RequestInit = {},
  customError?: string,
  fetchFn: FetchFn = fetchWithAuth
): Promise<any> {
  try {
    const resp = await fetchFn(url, reqOptions);
    if (resp.status === 204) return { data: {} };
    return resp.json().then((data: T) => ({ data }));
  } catch (e) {
    return {
      error: customError ? customError : e,
    };
  }
}

const provideTags = (
  defaultTags: TagDescription<
    'User' | 'Reports' | 'TargetSystems' | 'Devices'
  >[],
  error: FetchBaseQueryError | undefined,
  errorTags: TagDescription<
    'User' | 'Reports' | 'TargetSystems' | 'Devices'
  >[] = []
): TagDescription<'User' | 'Reports' | 'TargetSystems' | 'Devices'>[] => {
  return error ? errorTags : defaultTags;
};

export const ApiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    fetchFn: (input, init) => {
      //@ts-ignore
      return fetchWithAuth(input.url, init);
    },
  }),
  tagTypes: ['Devices', 'TargetSystems', 'Reports', 'User'],
  endpoints: (builder) => ({
    // QUERIES
    getConfig: builder.query<Config, null>({
      queryFn: async () => {
        return await requestWrapper<Config>(`/config.json`, {}, '', fetch);
      },
    }),
    getDevices: builder.query<MedisanteDevice[], GetDevicesArg>({
      queryFn: async ({ apiBaseURL, orgs, filters }) => {
        let searchQuery = getRequestQuery(orgs, filters);
        if (!validateFilters(filters)) {
          return {
            error: {
              status: 400,
              data: 'Search query must be at least four characters long.',
            },
          };
        }
        searchQuery = searchQuery.replace('includeArchived', 'archived');
        searchQuery = searchQuery.replace('onlyArchived', 'archived');

        searchQuery = searchQuery.replace('includeStock', 'instock');
        searchQuery = searchQuery.replace('onlyStock', 'instock');

        return await requestWrapper<MedisanteDevice[]>(
          `${apiBaseURL}/v1/orgs/devices${
            searchQuery ? '?refresh=true&' : '?refresh=true'
          }${searchQuery}`
        );
      },
      providesTags: ['Devices'],
    }),
    getDevicesById: builder.query<MedisanteDevice[], GetDevicesByIdArg>({
      queryFn: async ({ apiBaseURL, ids }) => {
        if (ids) {
          const promises = ids.map(
            async (id) =>
              await requestWrapper<MedisanteDevice>(
                `${apiBaseURL}/v1/devices/${encodeURIComponent(id)}`,
                {
                  headers: { Accept: 'application/json' },
                }
              )
          );

          const data = await Promise.all(promises);
          return { data: data.map((device) => device.data) };
        }
        return { data: [] };
      },
      providesTags: ['Devices'],
    }),
    getDevice: builder.query<MedisanteDevice, GetDeviceArg>({
      queryFn: async ({ apiBaseURL, id }) => {
        if (id) {
          return await requestWrapper<MedisanteDevice>(
            `${apiBaseURL}/v1/devices/${encodeURIComponent(id)}`
          );
        }
        return { error: { data: 'no id', status: 400 } };
      },
      providesTags: ['Devices'],
    }),
    deleteDevice: builder.mutation<MessageResponse, DeleteDeviceArg>({
      queryFn: async ({ apiBaseURL, id }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/devices/${id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      },
      invalidatesTags: [],
    }),
    getUser: builder.query<AuthUser | undefined, GetUserArg>({
      queryFn: async ({ apiBaseURL, id, token }) => {
        if (token) {
          const { data: respJSON } = await requestWrapper<AuthUser>(
            `${apiBaseURL}/v1/users/${encodeURIComponent(id)}`,
            {
              headers: { Accept: 'application/json' },
            }
          );

          const storedSelectedOrgs = localStorage.getItem('user.selected_org');

          const selectedOrgs = storedSelectedOrgs
            ? storedSelectedOrgs.split(';')
            : [];

          let usr: AuthUser = {
            ...respJSON,
            selectedOrgs,
          };
          if (usr.orgs) {
            usr.orgs = usr.orgs
              .filter((o) => {
                return (
                  hasOneOfOrgRoles(
                    respJSON,
                    [o.id],
                    [
                      Role.DeviceManager,
                      Role.DeviceViewer,
                      Role.OrganizationManager,
                    ]
                  ) ||
                  hasOneOfGlobalRoles(usr, [
                    Role.DeviceManager,
                    Role.DeviceViewer,
                    Role.OrganizationManager,
                  ])
                );
              })
              .sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
              );

            for (const org of usr.orgs) {
              if (!!org.deactivated) {
                usr.selectedOrgs =
                  usr.selectedOrgs.length > 0
                    ? usr.selectedOrgs
                    : [usr.orgs[0].id];
                break;
              }
            }
          }

          return { data: usr };
        }
        return { data: undefined };
      },
      providesTags: ['User'],
    }),
    updateUser: builder.mutation<MessageResponse, UpdateUserArg>({
      queryFn: async ({ apiBaseURL, id, data }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/users/${id}`,
          {
            method: 'PATCH',
            headers: { Accept: 'application/json' },
            body: JSON.stringify({ ...data }),
          }
        );
      },
    }),
    getTargetSystems: builder.query<MedisanteTargetSystem[], TargetSystemsArg>({
      queryFn: async ({ apiBaseURL, orgs, filters = [] }) => {
        const searchQuery = getRequestQuery(orgs, filters);

        return await requestWrapper<MedisanteTargetSystem[]>(
          `${apiBaseURL}/v1/orgs/target-systems${
            searchQuery ? '?' : ''
          }${searchQuery}`
        );
      },
      providesTags: ['TargetSystems'],
    }),
    getReports: builder.query<MedisanteOrgReport[], GetReportsArg>({
      queryFn: async ({ apiBaseURL, orgs, filters = [] }) => {
        if (
          filters.every(
            (f) =>
              validateFilters(filters) ||
              Object.values(categories)
                .map((c) => c.value)
                .includes(f.value)
          )
        ) {
          const query = getRequestQuery(orgs, filters);
          const resp = await requestWrapper<MedisanteOrgReport[]>(
            `${apiBaseURL}/v1/devices/reports?${query}`
          );
          return !resp.error && resp.data === null ? { data: [] } : resp;
        }

        return {
          error: {
            status: 400,
            data: 'Search query must be at least four characters long.',
          },
        };
      },
      providesTags: ['Reports'],
    }),
    getMetrics: builder.query<MedisanteMetric[], GetMetricsArg>({
      queryFn: async ({ apiBaseURL, deviceID }) => {
        const resp = await requestWrapper<MedisanteMetric[]>(
          `${apiBaseURL}/v1/devices/${encodeURIComponent(deviceID)}/metrics`,
          {
            headers: { Accept: 'application/json' },
          }
        );

        return !resp.error && resp.data === null ? { data: [] } : resp;
      },
      providesTags: ['Devices'],
    }),
    // MUTATIONS
    createOrg: builder.mutation<MessageResponse, CreateOrgArg>({
      queryFn: async ({ apiBaseURL, name }) => {
        return await requestWrapper<MessageResponse>(`${apiBaseURL}/v1/orgs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name, canCreateCare: false }),
        });
      },
      invalidatesTags: ['User'],
    }),
    deleteOrg: builder.mutation<MessageResponse, DeleteOrgArg>({
      queryFn: async ({ apiBaseURL, id }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/${id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      },
      invalidatesTags: ['User'],
    }),
    updateOrg: builder.mutation<MessageResponse, UpdateOrgArg>({
      queryFn: async ({ apiBaseURL, org }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/${org.id}`,
          {
            method: 'PATCH',
            headers: { Accept: 'application/json' },
            body: JSON.stringify(org),
          }
        );
      },
      invalidatesTags: ['User'],
    }),
    pinDevice: builder.mutation<MessageResponse, PinDeviceArg>({
      queryFn: async ({ orgId, apiBaseURL, deviceIDs, isPinned }) => {
        var request = {} as any;
        deviceIDs.forEach((d) => {
          if (!request[orgId]) {
            request[orgId] = { deviceIDs: [], isPinned: isPinned };
          }
          request[orgId].deviceIDs.push(d);
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=pin-device`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    archiveDevice: builder.mutation<MessageResponse, ArchiveDeviceArg>({
      queryFn: async ({ orgId, apiBaseURL, deviceIDs, isArchived }) => {
        var request = {} as any;
        deviceIDs.forEach((d) => {
          if (!request[orgId]) {
            request[orgId] = {
              deviceIDs: [],
              isArchived: isArchived,
            };
          }
          request[orgId].deviceIDs.push(d);
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=archive-device`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    resetMeasurementCount: builder.mutation<
      MessageResponse,
      ResetMeasurementCountArg
    >({
      queryFn: async ({ apiBaseURL, devices }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=reset-measurement-trip-count`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'Reports'], error),
    }),
    removeTsDevices: builder.mutation<MessageResponse, RemoveTsDevicesArg>({
      queryFn: async ({ id, apiBaseURL }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/target-systems/${id}/devices`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'TargetSystems'], error),
    }),
    removeTs: builder.mutation<MessageResponse, RemoveTsDevicesArg>({
      queryFn: async ({ id, apiBaseURL }) => {
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/target-systems/${id}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'TargetSystems'], error),
    }),
    sendTestMeasurement: builder.mutation<void, SendTestMeasurementArg>({
      queryFn: async ({
        apiBaseURL,
        deviceModel,
        measurementType,
        targetSystemId,
      }) => {
        const queryString = `deviceModel=${encodeURIComponent(
          deviceModel
        )}&measurementType=${encodeURIComponent(measurementType)}`;
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/target-systems/${targetSystemId}/send-test-measurements${
            queryString ? '?' : ''
          }${queryString}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['TargetSystems'], error),
    }),
    transferOwner: builder.mutation<MessageResponse, TransferOwner>({
      queryFn: async ({
        apiBaseURL,
        orgID,
        owner,
        deviceIDs,
        targetSystemIDs,
      }) => {
        var request = {} as any;
        deviceIDs.forEach((d) => {
          if (!request[orgID]) {
            request[orgID] = {
              deviceIDs: [],
              targetSystemIDs: targetSystemIDs,
              owner: owner,
            };
          }
          request[orgID].deviceIDs.push(d);
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=transfer-devices-ownership`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'TargetSystems'], error),
    }),
    createTargetSystem: builder.mutation<TSCreateWarnings, CreateTsArg>({
      queryFn: async ({ apiBaseURL, orgs, targetSystem }) => {
        if (!orgs || (orgs && orgs.length === 0)) {
          throw Error('There must be only one organisation selected!');
        }

        const targetSystems = orgs.map((org) => {
          return Object.assign({}, targetSystem, {
            owner: org,
          });
        });

        return await requestWrapper<TSCreateWarnings>(
          `${apiBaseURL}/v1/orgs/target-systems`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(targetSystems),
          }
        ).then((resp) => {
          if (resp.data && resp.data.warnings && resp.data.warnings.length) {
            resp.data.warnMessage = resp.data.warnings
              .filter(onlyUnique((f1, f2) => f1 === f2))
              .join('\n');
          }

          return resp;
        });
      },
      invalidatesTags: (result, error) => provideTags(['TargetSystems'], error),
    }),
    createCareTargetSystem: builder.mutation<TSCreateWarnings, CreateCareTsArg>(
      {
        queryFn: async ({ apiBaseURL, org }) => {
          if (!org) {
            throw Error('There must be only one organisation selected!');
          }

          return await requestWrapper<TSCreateWarnings>(
            `${apiBaseURL}/v1/orgs/${org}/care-target-systems`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          ).then((resp) => {
            if (resp.data && resp.data.warnings && resp.data.warnings.length) {
              resp.data.warnMessage = resp.data.warnings
                .filter(onlyUnique((f1, f2) => f1 === f2))
                .join('\n');
            }

            return resp;
          });
        },
        invalidatesTags: (result, error) =>
          provideTags(['TargetSystems'], error),
      }
    ),
    editTargetSystem: builder.mutation<TSCreateWarnings, EditTsArg>({
      queryFn: async ({ apiBaseURL, targetSystem }) => {
        const resp = await requestWrapper<TSCreateWarnings>(
          `${apiBaseURL}/v1/target-systems/${targetSystem.id}`,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(targetSystem),
          }
        );

        if (resp.data && resp.data.warnings && resp.data.warnings.length) {
          resp.data.warnMessage = resp.data.warnings
            .filter(onlyUnique((f1, f2) => f1 === f2))
            .join('\n');
        }

        return resp;
      },
      invalidatesTags: (result, error) => provideTags(['TargetSystems'], error),
    }),
    changeWeightUnit: builder.mutation<MessageResponse, ChangeWeightUnitArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, weightUnit }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              weightUnit: weightUnit,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-weight-unit`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    changeMinWeight: builder.mutation<MessageResponse, ChangeMinWeightArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, minWeight }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              minimumWeight: minWeight,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-minimum-weight`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    changeTimezone: builder.mutation<MessageResponse, ChangeTimezoneArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, timeZone }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              timeZone: timeZone,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-time-zone`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    configureHeartbeatSync: builder.mutation<
      MessageResponse,
      ConfigureHeartbeatSyncArg
    >({
      queryFn: async ({
        apiBaseURL,
        orgIDs,
        devices,
        timerDuration,
        retryCount,
        retryCountTime,
      }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              timerDuration: timerDuration,
              retryCount: retryCount,
              retryCountTime: retryCountTime,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-heartbeat-config`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    changeTargetSystem: builder.mutation<
      MessageResponse,
      ChangeTargetSystemArg
    >({
      queryFn: async ({ apiBaseURL, orgIDs, devices, targetSystemIDs }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              targetSystemIDs: targetSystemIDs,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-target-systems`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'TargetSystems', 'Reports'], error),
    }),
    resetDeviceToken: builder.mutation<MessageResponse, ResetDeviceTokenArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = { deviceIDs: [d.id] };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=reset-devices-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: [],
    }),
    changeFlagDevice: builder.mutation<MessageResponse, ChangeDeviceFlagArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, flags }) => {
        var request = {} as any;
        if (flags.length > 0) {
          devices.forEach((d) => {
            if (!request[d.owner]) {
              request[d.owner] = {
                deviceIDs: [d.id],
                status: flags,
              };
            } else {
              request[d.owner].deviceIDs.push(d.id);
            }
          });
          return await requestWrapper<MessageResponse>(
            `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(request),
            }
          );
        }

        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              status: [],
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    linkDevice: builder.mutation<MessageResponse, LinkDeviceArg>({
      queryFn: async ({ apiBaseURL, deviceIds, gatewayId }) => {
        var request = {} as any;
        deviceIds.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              sensors: [d.id],
              gateway: gatewayId,
              link: true, // link
            };
          } else {
            request[d.owner].sensors.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-linking`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          },
          'Linking failed!'
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    unLinkDevice: builder.mutation<MessageResponse, LinkDeviceArg>({
      queryFn: async ({ apiBaseURL, deviceIds, gatewayId }) => {
        var request = {} as any;
        deviceIds.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              sensors: [d.id],
              gateway: gatewayId,
              link: false, // unlink
            };
          } else {
            request[d.owner].sensors.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-linking`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          },
          'Unlinking failed!'
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    changeTestDevice: builder.mutation<MessageResponse, ChangeDeviceTestArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, isTest }) => {
        var request = {} as any;
        if (isTest) {
          devices.forEach((d) => {
            if (!request[d.owner]) {
              request[d.owner] = {
                deviceIDs: [d.id],
                testdevice: isTest,
              };
            } else {
              request[d.owner].deviceIDs.push(d.id);
            }
          });
          return await requestWrapper<MessageResponse>(
            `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(request),
            }
          );
        }
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              testdevice: false,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    changeMuteDevice: builder.mutation<MessageResponse, MuteDeviceArg>({
      queryFn: async ({ apiBaseURL, orgIDs, devices, mute }) => {
        var request = {} as any;
        if (mute) {
          devices.forEach((d) => {
            if (!request[d.owner]) {
              request[d.owner] = {
                deviceIDs: [d.id],
                muted: mute,
              };
            } else {
              request[d.owner].deviceIDs.push(d.id);
            }
          });
          return await requestWrapper<MessageResponse>(
            `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(request),
            }
          );
        }
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              muted: false,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-properties`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) =>
        provideTags(['Devices', 'Reports'], error),
    }),
    blockUnblockDevice: builder.mutation<MessageResponse, BlockUnblockArg>({
      queryFn: async ({ apiBaseURL, devices, block }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              block: block,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=block-device`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    stockDevice: builder.mutation<MessageResponse, StockDeviceArg>({
      queryFn: async ({ apiBaseURL, devices, stock }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              inStock: stock,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=stock-device`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
    unlikedGateway: builder.mutation<MessageResponse, UnlinkedMode>({
      queryFn: async ({ apiBaseURL, devices, allow }) => {
        var request = {} as any;
        devices.forEach((d) => {
          if (!request[d.owner]) {
            request[d.owner] = {
              deviceIDs: [d.id],
              allow: allow,
            };
          } else {
            request[d.owner].deviceIDs.push(d.id);
          }
        });
        return await requestWrapper<MessageResponse>(
          `${apiBaseURL}/v1/orgs/jobs?action=change-devices-unlinked-mode`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(request),
          }
        );
      },
      invalidatesTags: (result, error) => provideTags(['Devices'], error),
    }),
  }),
});

export const {
  // QUERIES
  useGetDevicesQuery,
  useGetDeviceQuery,
  useGetTargetSystemsQuery,
  useGetReportsQuery,
  useGetMetricsQuery,
  useGetDevicesByIdQuery,
  useGetUserQuery,
  useGetConfigQuery,
  // MUTATIONS
  usePinDeviceMutation,
  useArchiveDeviceMutation,
  useRemoveTsDevicesMutation,
  useRemoveTsMutation,
  useTransferOwnerMutation,
  useSendTestMeasurementMutation,
  useCreateTargetSystemMutation,
  useCreateCareTargetSystemMutation,
  useEditTargetSystemMutation,
  useChangeWeightUnitMutation,
  useChangeMinWeightMutation,
  useChangeTimezoneMutation,
  useConfigureHeartbeatSyncMutation,
  useChangeTargetSystemMutation,
  useResetDeviceTokenMutation,
  useChangeFlagDeviceMutation,
  useUnLinkDeviceMutation,
  useLinkDeviceMutation,
  useCreateOrgMutation,
  useDeleteOrgMutation,
  useUpdateOrgMutation,
  useChangeMuteDeviceMutation,
  useChangeTestDeviceMutation,
  useDeleteDeviceMutation,
  useUpdateUserMutation,
  useResetMeasurementCountMutation,
  useBlockUnblockDeviceMutation,
  useStockDeviceMutation,
  useUnlikedGatewayMutation,
} = ApiSlice;

export const apiInvalidateTags = ApiSlice.util.invalidateTags;
